import { icon } from './icons';
import type { Navigate } from './router';

export interface PageShellOptions {
  title: string;
  subtitle?: string;
  body: string;
}

/**
 * The persistent nav — Play / Customize / Account / Home — always in this
 * order, always all four. Previously home.ts and page-shell.ts each hand-rolled
 * a *different* 3-item header (home.ts: Play/Customise/Account, no Home;
 * page-shell.ts: Play/Account/Home, no Customise), so which links were even
 * reachable depended on which page you happened to be on — Customize was
 * simply absent everywhere except the homepage. One shared list here means
 * every page's nav is now identical by construction, not by convention.
 */
const NAV_ITEMS: { key: string; label: string; route: string }[] = [
  { key: 'play', label: 'Play', route: 'play' },
  { key: 'customise', label: 'Customize', route: 'customise' },
  { key: 'account', label: 'Account', route: 'account' },
  { key: 'home', label: 'Home', route: 'home' },
];

/**
 * Shared header markup — brand, primary nav, and the leaderboard shortcut —
 * used by every page (via `renderPageShell` below) *and* by `home.ts`
 * directly, since the homepage's hero doesn't fit the generic
 * title/subtitle/body shape `renderPageShell` renders. One template means
 * the nav can never again drift between pages the way it did before.
 *
 * The header is a three-column grid on desktop: brand (left), nav (centre),
 * and a right-hand actions group. The leaderboard pill lives *inside* that
 * `.header-actions` group, NOT as a fourth top-level grid child — an earlier
 * version emitted it as a sibling of `.header-actions`, which overflowed the
 * three-column track: the pill was crammed into the nav's column and the
 * profile icon wrapped onto a second row below "Play" (owner report,
 * 2026-08-18). Grouping them keeps the header a single row with exactly three
 * grid children. On narrow viewports CSS lifts `.primary-nav` and
 * `.leaderboard-strip` out into a fixed bottom bar (see the `62rem` media
 * query in app.css); because both are `position: fixed` there, their DOM
 * nesting no longer matters and this grouping is desktop-only in effect.
 */
export function siteHeaderHtml(activeKey: string, extraHeaderActions = ''): string {
  const navLinks = NAV_ITEMS.map(
    (item) =>
      `<button class="nav-link${activeKey === item.key ? ' is-active' : ''}" type="button" data-nav="${item.route}">${item.label}</button>`,
  ).join('');

  return `
    <header class="site-header">
      <a class="brand" href="#/home" aria-label="Timing Games home">
        <span class="brand-mark" aria-hidden="true"><i></i><i></i><i></i></span>
        <span>TIMING<span>GAMES</span></span>
      </a>

      <nav class="primary-nav" aria-label="Primary navigation">${navLinks}</nav>

      <div class="header-actions">
        ${extraHeaderActions}
        <a class="leaderboard-strip${activeKey === 'leaderboard' ? ' is-active' : ''}" href="#/leaderboard" aria-label="Leaderboard">
          ${icon('trophy')}<span>Leaderboard</span>
        </a>
        <button class="icon-button" type="button" data-nav="account" aria-label="Account">
          ${icon('user')}
        </button>
      </div>
    </header>
  `;
}

/**
 * Wires every `[data-nav]` *button* in `container` to the router's
 * `navigate()`. Real anchors (`.brand`, `.leaderboard-strip`) are left alone —
 * they carry a real `href="#/..."` and the router already resolves on any
 * `hashchange`, so a plain anchor click just works without JS, the same way
 * `.brand` always has.
 */
export function bindSiteNav(container: HTMLElement, navigate: Navigate): void {
  container.querySelectorAll<HTMLButtonElement>('button[data-nav]').forEach((button) => {
    button.addEventListener('click', () => navigate(button.dataset.nav as string));
  });
}

/** Renders the shared header/back-nav chrome and returns the mounted content element for further use. */
export function renderPageShell(
  container: HTMLElement,
  navigate: Navigate,
  activeKey: string,
  options: PageShellOptions,
): HTMLElement {
  container.innerHTML = `
    <div class="app-shell">
      ${siteHeaderHtml(activeKey)}

      <main id="main-content" class="main-content page-content">
        <div class="page-heading">
          <h1>${options.title}</h1>
          ${options.subtitle ? `<p>${options.subtitle}</p>` : ''}
        </div>
        <div class="page-body" id="page-body">${options.body}</div>
      </main>
    </div>
  `;

  bindSiteNav(container, navigate);

  return container.querySelector<HTMLElement>('#page-body')!;
}
